import { Link } from 'react-router';
import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { config } from '@/config';

/**
 * AuthShell — centered-card scaffold for the unauthenticated flow.
 *
 * Used by SignIn (already migrated), AccountSignUp, ForgotPassword,
 * ResetPassword, MustVerify, VerifyEmailLanding, AuthVerifySuccess/Fail,
 * SignOut, AcceptInvitation. App.tsx skips the AppShell for these
 * pages (no user), so AuthShell provides the bare scaffolding:
 * full-viewport centering on bg-background, brand N tile + heading,
 * optional subtitle, card body, optional below-card link row.
 *
 * Keeps the visual lineage with the rest of the new shell (same brand
 * tile as SideNav uses for the app name).
 */
export interface AuthShellProps {
  /** Heading above the card (defaults to the app name). */
  title?: ReactNode;
  /** Optional one-line subtitle under the title. */
  subtitle?: ReactNode;
  /** Card body — usually a <form>. */
  children: ReactNode;
  /** Optional row of links below the card, e.g. "Don't have an account? Sign up". */
  footer?: ReactNode;
  /** Max width on the inner card. Defaults to max-w-sm (~24rem). */
  width?: 'sm' | 'md' | 'lg';
  /** Hide the brand mark + title block (for pages that have their own header). */
  bare?: boolean;
  className?: string;
}

const WIDTH_CLASS: Record<NonNullable<AuthShellProps['width']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
};

export function AuthShell({
  title = config.appName,
  subtitle,
  children,
  footer,
  width = 'sm',
  bare = false,
  className,
}: AuthShellProps) {
  return (
    <main className="min-h-svh flex flex-col items-center justify-center bg-background px-4 py-12">
      <div className={cn('w-full', WIDTH_CLASS[width], className)}>
        {!bare ? (
          <div className="flex flex-col items-center mb-6">
            <Link to="/signin" className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center text-primary-foreground font-bold mb-3">
                {config.appName.charAt(0)}
              </div>
            </Link>
            <h1 className="text-xl font-semibold tracking-tight text-center">{title}</h1>
            {subtitle ? (
              <p className="text-sm text-muted-foreground mt-1 text-center max-w-prose">
                {subtitle}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="bg-card border border-border rounded-lg shadow-sm">{children}</div>

        {footer ? (
          <div className="text-sm text-muted-foreground text-center mt-5">{footer}</div>
        ) : null}
      </div>
    </main>
  );
}
